import { Bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import { Badge } from '@flipster/react/design-system/badge';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';

import { BunjaInstanceValueInspector } from '../misc/inspector';
import { StoreAsTmpButton, getStoreAsTmpFn } from '../misc/store-as-tmp-button';
import { BunjaDependencyGraph } from './bunja-dependency-graph';
import { SelectedBunjaIdContext, bunjaInstancesBunja, dagDevtoolsBunja } from './state';

export const BunjaDetails = () => {
  const { bunjasAtom } = useBunja(dagDevtoolsBunja);
  const bunjas = useAtomValue(bunjasAtom);
  const { instancesAtom } = useBunja(bunjaInstancesBunja);
  const instances = useAtomValue(instancesAtom);
  const selectedBunjaId = useContext(SelectedBunjaIdContext);
  const selectedBunja = bunjas[selectedBunjaId];
  if (!selectedBunja) return null;
  return (
    <div className={flex({ direction: 'column', flex: 1, minH: 0, overflow: 'auto', p: 4, gap: 20 })}>
      <BunjaInformation selectedBunja={selectedBunja} selectedBunjaId={selectedBunjaId} />
      {selectedBunja.baked && <BakedBunjaDetails selectedBunja={selectedBunja} instances={instances} />}
    </div>
  );
};

interface BunjaInformationProps {
  selectedBunja: Bunja<unknown>;
  selectedBunjaId: string;
}
const BunjaInformation = ({ selectedBunja, selectedBunjaId }: BunjaInformationProps) => {
  return (
    <div
      className={flex({
        p: 3,
        rounded: 4,
        bgColor: 'background.default.level-1',
        gap: 4,
        alignItems: 'center',
        flexWrap: 'wrap',
      })}
    >
      <Badge type='tint' color={selectedBunja.baked ? 'positive' : 'neutral'}>
        {selectedBunja.baked ? 'Baked' : 'Not Baked'}
      </Badge>
      <span
        className={flex({
          textStyle: 'Text/S/Monospace/Regular',
          color: 'inverse.white',
        })}
      >
        {selectedBunja.debugLabel || 'Anonymous Bunja'}
      </span>
      <span
        className={flex({
          textStyle: 'Text/S/Monospace/Regular',
          color: 'content.default.level-3',
        })}
      >
        #{selectedBunjaId}
      </span>
    </div>
  );
};

interface BakedBunjaDetailsProps {
  selectedBunja: BunjaInformationProps['selectedBunja'];
  instances: Record<string, { value: unknown }>;
}
const BakedBunjaDetails = ({ selectedBunja, instances }: BakedBunjaDetailsProps) => {
  const hasParents = selectedBunja.requiredBunjas.length > 0;
  if (!hasParents) return <BunjaInstances instances={instances} />;
  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        alignItems: 'flex-start',
        smallDesktop: { flexDirection: 'row' },
      })}
    >
      <div className={flex({ direction: 'column', flex: 1, minW: 0 })}>
        <BunjaDependencyGraph bunja={selectedBunja} />
      </div>
      <div className={flex({ direction: 'column', flex: 1, minW: 0 })}>
        <BunjaInstances instances={instances} />
      </div>
    </div>
  );
};

interface BunjaInstancesProps {
  instances: Record<string, { value: unknown }>;
}
const BunjaInstances = ({ instances }: BunjaInstancesProps) => {
  const instanceEntries = Object.entries(instances);
  return (
    <div className={flex({ direction: 'column', px: 8, pb: 32, gap: 4 })}>
      <div
        className={flex({
          textStyle: 'Text/L/Monospace/Bold',
          color: 'inverse.white',
        })}
      >
        Instances ({instanceEntries.length})
      </div>
      <div className={flex({ direction: 'column', gap: 2 })}>
        {instanceEntries.map(([instanceId, instance]) => {
          const storeAsTmp = getStoreAsTmpFn(instanceId, instance.value);
          return (
            <div
              key={instanceId}
              className={stack({
                gap: 4,
                textStyle: 'Text/S/Monospace/Regular',
              })}
            >
              <div className={flex({ alignItems: 'center', justifyContent: 'space-between' })}>
                <span className={css({ color: 'content.default.level-3' })}>{instanceId}</span>
                <StoreAsTmpButton onClick={storeAsTmp} />
              </div>
              <BunjaInstanceValueInspector value={instance.value} />
            </div>
          );
        })}
        {instanceEntries.length === 0 && (
          <div
            className={flex({
              p: 4,
              justifyContent: 'center',
              color: 'content.default.level-3',
              textStyle: 'Text/M/Regular',
            })}
          >
            No instances found
          </div>
        )}
      </div>
    </div>
  );
};
